import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { selectedShippingAddress } from "@atoms";
import { getMergeCandidates, sendMergeOtp, confirmMerge, getImagesDB, logMergeEvent, mergeDebug } from "@api";
import "./style.css";

/**
 * MergeSnackbar — Path B of the gallery-merge flow (split-cart recovery).
 *
 * Offers to pull a previous cart's images into the gallery the user is
 * currently building. Trigger: a live candidate cart exists for the user's
 * known contact (localStorage userEmail / the checkout address form).
 *
 * Dismissal semantics (approved spec): no ✕ — tapping anywhere outside the
 * pill or 10s without interaction soft-hides it (returns next page load);
 * only the sheet's "אולי מאוחר יותר" snoozes for 24h.
 *
 * Also renders the Path A success toast when the review page is entered with
 * router state { mergeToast } after an adopt.
 */

const MERGE_SNOOZE_KEY = "mergeSnoozeUntil";
const MERGE_SNOOZE_MS = 24 * 60 * 60 * 1000;
const PILL_AUTOHIDE_MS = 10000;
const TOAST_MS = 5000;

const BubbleIcon = () => (
  <svg viewBox="0 0 25.945 26.07" fill="currentColor" aria-hidden="true">
    <path d="M22.165,3.789A12.921,12.921,0,0,0,1.833,19.376L0,26.07l6.848-1.8a12.908,12.908,0,0,0,6.173,1.572h.005A12.924,12.924,0,0,0,22.165,3.789ZM13.027,23.664h0a10.721,10.721,0,0,1-5.465-1.5l-.392-.233L3.1,23l1.085-3.962-.255-.406a10.737,10.737,0,1,1,9.1,5.032Z" fillRule="evenodd" />
  </svg>
);

const EnvelopeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M22 7l-10 6L2 7" />
  </svg>
);

// The cart success V mark (SuccessReveal geometry — see blends-v-mark skill).
const VMark = () => (
  <svg className="mgs-vmark" viewBox="0 0 52 52" aria-hidden="true">
    <circle cx="26" cy="26" r="24" />
    <path d="M15 27l7.5 7.5L37 19" />
  </svg>
);

const snoozed = () => {
  const until = parseInt(localStorage.getItem(MERGE_SNOOZE_KEY) || "0", 10);
  return until && Date.now() < until;
};

const MergeSnackbar = ({ onMerged = null }) => {
  const location = useLocation();
  const address = useRecoilValue(selectedShippingAddress);

  const [candidate, setCandidate] = useState(null);
  const [ui, setUi] = useState("hidden"); // hidden | pill | sheet | otp | working | toast
  const [toastText, setToastText] = useState("");
  const [channel, setChannel] = useState("sms");
  const [code, setCode] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [expiryTime, setExpiryTime] = useState(0);
  const codeRefs = [useRef(), useRef(), useRef(), useRef()];
  const pillRef = useRef(null);
  const uiRef = useRef(ui);
  uiRef.current = ui;

  // ---- Path A landing toast (adopt happened during onboarding) ----
  useEffect(() => {
    const msg = location.state && location.state.mergeToast;
    if (msg) {
      setToastText(msg);
      setUi("toast");
      window.history.replaceState({}, ""); // one-shot
      const t = setTimeout(() => setUi((u) => (u === "toast" ? "hidden" : u)), TOAST_MS);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- candidate check ----
  const contactEmail = (address && address.email) || localStorage.getItem("userEmail") || "";
  const contactPhone = (address && address.phoneNumber) || "";

  useEffect(() => {
    let cancelled = false;
    if (snoozed()) return undefined;
    if (!localStorage.getItem("sessionId")) return undefined;
    if (uiRef.current !== "hidden") return undefined;

    getMergeCandidates({ email: contactEmail, phone: contactPhone, source: "snackbar" })
      .then((res) => {
        mergeDebug(`snackbar candidates (email=${!!contactEmail} phone=${!!contactPhone}) -> ${JSON.stringify(res)}`);
        if (cancelled || !res || !res.found) return;
        setCandidate(res);
        setUi("pill");
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactEmail, contactPhone]);

  // ---- pill: 10s auto-hide + tap-outside soft close ----
  useEffect(() => {
    if (ui !== "pill") return undefined;
    const timer = setTimeout(() => setUi("hidden"), PILL_AUTOHIDE_MS);
    const onDocClick = (e) => {
      if (pillRef.current && !pillRef.current.contains(e.target)) setUi("hidden");
    };
    document.addEventListener("click", onDocClick, true);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("click", onDocClick, true);
    };
  }, [ui]);

  // ---- timers ----
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const t = setTimeout(() => setResendCooldown((p) => p - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  useEffect(() => {
    if (expiryTime <= 0) return undefined;
    const t = setTimeout(() => setExpiryTime((p) => p - 1), 1000);
    return () => clearTimeout(t);
  }, [expiryTime]);

  const defer = () => {
    localStorage.setItem(MERGE_SNOOZE_KEY, String(Date.now() + MERGE_SNOOZE_MS));
    logMergeEvent("deferred", "snackbar");
    setUi("hidden");
  };

  const sendCode = async (ch) => {
    setLoading(true);
    setError("");
    try {
      await sendMergeOtp(ch, { email: contactEmail, phone: contactPhone, source: "snackbar" });
      setChannel(ch);
      setUi("otp");
      setCode(["", "", "", ""]);
      setResendCooldown(60);
      setExpiryTime(300);
      setTimeout(() => codeRefs[0].current?.focus(), 120);
    } catch (err) {
      setError(err.response?.data?.error || "שגיאה בשליחת הקוד");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, "").slice(0, 4);
      const next = ["", "", "", ""];
      for (let i = 0; i < 4; i++) next[i] = digits[i] || "";
      setCode(next);
      setError("");
      codeRefs[Math.min(digits.length, 3)].current?.focus();
      return;
    }
    const digit = value.replace(/\D/g, "");
    setCode((prev) => {
      const n = [...prev];
      n[index] = digit;
      return n;
    });
    setError("");
    if (digit && index < 3) codeRefs[index + 1].current?.focus();
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      codeRefs[index - 1].current?.focus();
    }
    if (e.key === "Enter") handleVerify();
  };

  const showToast = useCallback((text) => {
    setToastText(text);
    setUi("toast");
    setTimeout(() => setUi((u) => (u === "toast" ? "hidden" : u)), TOAST_MS);
  }, []);

  const handleVerify = async () => {
    const fullCode = code.join("");
    if (fullCode.length !== 4) {
      setError("יש להזין קוד בן 4 ספרות");
      return;
    }
    if (expiryTime <= 0) {
      setError("הקוד פג תוקף, בקשו קוד חדש");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const result = await confirmMerge(fullCode, { email: contactEmail, phone: contactPhone, source: "snackbar" });
      mergeDebug(`confirm -> ${JSON.stringify(result)}`);
      setUi("working");
      if (result.mode === "adopt" && result.sessionId && result.uid) {
        // Edge: current cart was empty — the old cart simply becomes active.
        localStorage.setItem("uniqueUserId", result.uid);
        localStorage.setItem("sessionId", result.sessionId);
        localStorage.setItem("dataExist", 1);
      }
      await getImagesDB();
      if (onMerged) onMerged(result);
      setCandidate(null);
      showToast(
        result.mode === "adopt"
          ? "הגלריה שלך חזרה ואפשר להמשיך מאיפה שעצרת"
          : `${result.moved} תמונות שוחזרו לגלריה שלך`
      );
    } catch (err) {
      setError(err.response?.data?.error || "קוד שגוי, נסו שוב");
      setUi("otp");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || loading) return;
    await sendCode(channel);
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  if (ui === "hidden") return null;

  return (
    <div className="mgs" dir="rtl">
      {ui === "pill" && candidate && (
        <div className="mgs-pill" ref={pillRef} role="status">
          <span className="mgs-pill-ico" aria-hidden="true">🖼️</span>
          <span className="mgs-pill-txt">
            <b>{`נמצאו עוד ${candidate.imageCount} תמונות`}</b>
            בגלריה נוספת המשויכת אליך
          </span>
          <button type="button" className="mgs-pill-cta" onClick={() => setUi("sheet")}>
            שחזור
          </button>
        </div>
      )}

      {(ui === "sheet" || ui === "otp") && candidate && (
        <>
          <div className="mgs-scrim" onClick={() => setUi("pill")} />
          <div className="mgs-sheet" role="dialog" aria-modal="true">
            <div className="mgs-grip" />
            {ui === "sheet" ? (
              <>
                <h4 className="mgs-title">שחזור תמונות לגלריה</h4>
                <p className="mgs-body">
                  מצאנו גלריה נוספת עם <b>{candidate.imageCount} תמונות</b> המשויכת לפרטים שלך.
                  <br />
                  כדי לצרף אותן, נאמת שהיא שלך:
                </p>
                {candidate.maskedPhone && (
                  <button type="button" className="mgs-opt" disabled={loading} onClick={() => sendCode("sms")}>
                    <span className="mgs-opt-ico"><BubbleIcon /></span>
                    <span className="mgs-opt-txt">
                      קוד ב-SMS
                      <small>{candidate.maskedPhone}</small>
                    </span>
                  </button>
                )}
                {candidate.maskedEmail && (
                  <button type="button" className="mgs-opt" disabled={loading} onClick={() => sendCode("email")}>
                    <span className="mgs-opt-ico"><EnvelopeIcon /></span>
                    <span className="mgs-opt-txt">
                      קוד באימייל
                      <small>{candidate.maskedEmail}</small>
                    </span>
                  </button>
                )}
                {error && <p className="mgs-error">{error}</p>}
                <button type="button" className="mgs-defer" onClick={defer}>
                  אולי מאוחר יותר
                </button>
              </>
            ) : (
              <>
                <h4 className="mgs-title">הזינו את הקוד</h4>
                <p className="mgs-body">
                  שלחנו קוד בן 4 ספרות ל-<b dir="ltr" style={{ unicodeBidi: "isolate" }}>{channel === "email" ? candidate.maskedEmail : candidate.maskedPhone}</b>
                </p>
                <div className="mgs-code-row" dir="ltr">
                  {code.map((digit, i) => (
                    <input
                      key={i}
                      ref={codeRefs[i]}
                      type="text"
                      inputMode="numeric"
                      autoComplete={i === 0 ? "one-time-code" : "off"}
                      className={`mgs-code-input${digit ? " filled" : ""}`}
                      value={digit}
                      onChange={(e) => handleCodeChange(i, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(i, e)}
                      maxLength={i === 0 ? 4 : 1}
                    />
                  ))}
                </div>
                {expiryTime > 0 ? (
                  <p className="mgs-timer">הקוד תקף ל-{formatTime(expiryTime)}</p>
                ) : (
                  <p className="mgs-timer mgs-timer--expired">הקוד פג תוקף</p>
                )}
                {error && <p className="mgs-error">{error}</p>}
                <button
                  type="button"
                  className="mgs-btn"
                  onClick={handleVerify}
                  disabled={loading || code.join("").length !== 4}
                >
                  {loading ? <span className="mgs-spinner mgs-spinner--btn" /> : "אימות ושחזור"}
                </button>
                <div className="mgs-links">
                  <button type="button" className="mgs-link" onClick={handleResend} disabled={resendCooldown > 0}>
                    {resendCooldown > 0 ? `שליחה חוזרת בעוד ${resendCooldown} שניות` : "שלח קוד חדש"}
                  </button>
                  <button type="button" className="mgs-link" onClick={() => { setUi("sheet"); setError(""); }}>
                    חזרה
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {ui === "working" && (
        <div className="mgs-toast">
          <span className="mgs-spinner mgs-spinner--toast" />
          מצרפים את התמונות לגלריה…
        </div>
      )}

      {ui === "toast" && (
        <div className="mgs-toast">
          <VMark />
          {toastText}
        </div>
      )}
    </div>
  );
};

export default MergeSnackbar;
